import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import Attachment from "./Attachment";
import MessageActionsMenu from "./MessageActionsMenu";
import MessageEntityCard from "./MessageEntityCard";
import SiteMediaViewer from "../../components/media/SiteMediaViewer";
import {
    downloadBlob,
    fetchChatAttachment,
    getChatAttachmentName,
    getChatAttachmentMimeType,
    getChatAttachmentType,
    hasSecureChatAttachment,
    shareBlobFile,
} from "../../utils/chatAttachmentStore";

const getId = (value) => String(value?._id || value || "");

const getForwardedSenderName = (message) => {
    const forwardedFrom = message?.forwardedFrom;
    if (!forwardedFrom || typeof forwardedFrom !== "object") return "";

    if (forwardedFrom.senderName) return String(forwardedFrom.senderName).trim();

    const sender = forwardedFrom.sender;
    if (sender && typeof sender === "object") {
        const name = [sender.firstName, sender.lastName].filter(Boolean).join(" ").trim();
        return name || sender.email || sender.username || "";
    }

    return "";
};

const getForwardedInfo = (message, currentUserId, chats = []) => {
    const forwardedFrom = message?.forwardedFrom;
    if (!forwardedFrom || typeof forwardedFrom !== "object") return null;

    const forwarded = !!(
        getId(forwardedFrom.messageId) ||
        getId(forwardedFrom.chatId) ||
        getId(forwardedFrom.sender) ||
        forwardedFrom.senderName
    );

    if (!forwarded) return null;

    const senderId = getId(forwardedFrom.sender);
    const senderName = getForwardedSenderName(message);
    const fromMe = !!senderId && senderId === getId(currentUserId);
    const authorText = fromMe ? "вас" : (senderName || "пользователя");

    const chatType = String(forwardedFrom.chatType || "");
    const isDirect = chatType === "direct" && forwardedFrom.isGroup !== true;
    const isSaved = chatType === "saved";
    const showSourceChat = !isDirect && !isSaved && !!forwardedFrom.chatTitle;
    const sourceChatId = getId(forwardedFrom.chatId);
    const sourceMessageId = getId(forwardedFrom.messageId);
    const sourceAccessible = !!sourceChatId && (chats || []).some(
        (chat) => getId(chat?._id) === sourceChatId
    );

    return {
        origin: forwardedFrom,
        authorText,
        showSourceChat,
        sourceTitle: String(forwardedFrom.chatTitle || "").trim(),
        sourceAccessible: sourceAccessible && !!sourceMessageId,
    };
};

const isInteractiveTarget = (target) => !!target?.closest?.(
    'button, a, input, textarea, select, label, [role="button"], [data-chat-no-select="true"]'
);

const canSelectMessage = (message) => !!(
    message?._id &&
    !String(message._id).startsWith("local:") &&
    !message?.isDeleted &&
    message?.type !== "call" &&
    !message?.call
);

const isImageAttachment = (message) => (
    hasSecureChatAttachment(message) &&
    getChatAttachmentType(message) === "image"
);

const getSenderName = (sender, currentUserId, guestSender = null) => {
    if (guestSender?.name) return guestSender.name;
    if (!sender) return "Собеседник";
    if (getId(sender) === getId(currentUserId)) return "Вы";

    const name = [sender.firstName, sender.lastName].filter(Boolean).join(" ").trim();

    return name || sender.email || sender.username || "Собеседник";
};

const formatTime = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
    });
};

const getDayKey = (value) => {
    const date = new Date(value || 0);
    if (Number.isNaN(date.getTime())) return "";
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const formatDayLabel = (value) => {
    const date = new Date(value || 0);
    if (Number.isNaN(date.getTime())) return "";

    const now = new Date();
    const todayKey = getDayKey(now);
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    const key = getDayKey(date);
    if (key === todayKey) return "Сегодня";
    if (key === getDayKey(yesterday)) return "Вчера";

    return date.toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
    });
};

const formatCallStatus = (status) => {
    switch (status) {
        case "ringing": return "вызов";
        case "accepted": return "соединение установлено";
        case "missed": return "пропущен";
        case "ended": return "завершён";
        case "rejected": return "отклонён";
        case "busy": return "занято";
        case "failed": return "ошибка";
        case "cancelled": return "отменён";
        default: return status || "—";
    }
};

const parseLegacyReply = (text = "") => {
    if (!text.startsWith("↩️ ")) return null;

    const index = text.indexOf("\n");
    const head = index > -1 ? text.slice(0, index) : text;
    const body = index > -1 ? text.slice(index + 1) : "";
    const match = head.match(/^↩️\s+(.+?):\s+(.*)$/);

    return {
        author: match?.[1] || "Собеседник",
        snippet: match?.[2] || head.replace(/^↩️\s*/, ""),
        body,
    };
};

const getReply = (message) => {
    if (message?.replyTo?.messageId || message?.replyTo?.author || message?.replyTo?.snippet) {
        return {
            author: message.replyTo.author || "Собеседник",
            snippet: message.replyTo.snippet || "",
            body: message.text || "",
        };
    }

    return parseLegacyReply(message?.text || "");
};

const canGroupWithPrevious = (message, previous) => {
    if (!message || !previous) return false;
    if (message.type === "call" || message.call || previous.type === "call" || previous.call) return false;

    const currentSender = getId(message.sender) || `guest:${message.guestSender?.visitorId || message.guestSender?.publicId || ""}`;
    const previousSender = getId(previous.sender) || `guest:${previous.guestSender?.visitorId || previous.guestSender?.publicId || ""}`;
    if (!currentSender || currentSender !== previousSender) return false;

    const currentTime = new Date(message.createdAt || 0).getTime();
    const previousTime = new Date(previous.createdAt || 0).getTime();
    if (!currentTime || !previousTime) return false;

    return currentTime - previousTime >= 0 && currentTime - previousTime <= 5 * 60 * 1000;
};

export default function ChatMessages({
    messages = [],
    currentUserId,
    selectedId,
    selectedMessageIds = [],
    selectionMode = false,
    chats = [],
    onReply,
    onDelete,
    onForward,
    onOpenForwardOrigin,
    onSelectMessage,
    onAddMessageSelection,
    focusMessageId = null,
    officeId = null,
}) {
    const [openImageId, setOpenImageId] = useState(null);
    const [actionMenu, setActionMenu] = useState({ message: null, point: null });
    const rootRef = useRef(null);
    const longPressTimerRef = useRef(null);
    const longPressStartRef = useRef(null);
    const dragRef = useRef({ active: false, moved: false, pointerY: 0, startX: 0, startY: 0, startMessage: null });
    const dragRafRef = useRef(null);
    const suppressClickRef = useRef(false);
    const selectedSet = useMemo(() => new Set((selectedMessageIds || []).map(getId)), [selectedMessageIds]);

    const imageMessages = useMemo(
        () => messages.filter(isImageAttachment),
        [messages]
    );

    const dayGroups = useMemo(() => {
        const groups = [];

        (messages || []).forEach((message) => {
            const dayKey = getDayKey(message?.createdAt) || `unknown:${getId(message?._id)}`;
            const previousGroup = groups[groups.length - 1];

            if (!previousGroup || previousGroup.dayKey !== dayKey) {
                groups.push({
                    dayKey,
                    label: formatDayLabel(message?.createdAt),
                    messages: [message],
                });
                return;
            }

            previousGroup.messages.push(message);
        });

        return groups;
    }, [messages]);

    const openImageIndex = useMemo(() => {
        if (!openImageId) return 0;

        const index = imageMessages.findIndex(
            (message) => String(message?._id || "") === String(openImageId)
        );

        return index >= 0 ? index : 0;
    }, [imageMessages, openImageId]);

    const resolveImage = useCallback(
        (message) => fetchChatAttachment({ message }),
        []
    );

    const handleImageIndexChange = useCallback((_nextIndex, nextMessage) => {
        setOpenImageId(nextMessage?._id || null);
    }, []);

    const handleDownload = useCallback(async (message) => {
        const asset = await fetchChatAttachment({ message, download: true, useCache: false });
        downloadBlob({
            blob: asset.blob,
            filename: asset.filename || getChatAttachmentName(message),
        });
    }, []);

    const handleShare = useCallback(async (message) => {
        const asset = await fetchChatAttachment({ message });
        await shareBlobFile({
            blob: asset.blob,
            filename: asset.filename || getChatAttachmentName(message),
            mimeType: asset.mimeType || getChatAttachmentMimeType(message),
            title: "Фото из чата",
        });
    }, []);

    const closeActionMenu = useCallback(() => {
        setActionMenu({ message: null, point: null });
    }, []);

    const openActionMenu = useCallback((message, x, y) => {
        if (!message || !canSelectMessage(message)) return;

        setActionMenu({
            message,
            point: { x: Number(x || 0), y: Number(y || 0) },
        });
    }, []);

    const addMessageAtPoint = useCallback((clientX, clientY) => {
        const node = document.elementFromPoint(clientX, clientY)?.closest?.("[data-chat-message-id]");
        const messageId = node?.getAttribute?.("data-chat-message-id");
        if (!messageId) return;

        const message = (messages || []).find((item) => getId(item?._id) === getId(messageId));
        if (!canSelectMessage(message)) return;

        onAddMessageSelection?.(message);
    }, [messages, onAddMessageSelection]);

    const stopDragSelection = useCallback(() => {
        dragRef.current.active = false;
        dragRef.current.pointerY = 0;
        document.body.classList.remove("chat-drag-selecting-AO");

        if (dragRafRef.current) {
            window.cancelAnimationFrame(dragRafRef.current);
            dragRafRef.current = null;
        }
    }, []);

    const runAutoScroll = useCallback(() => {
        if (!dragRef.current.active || !dragRef.current.moved) {
            dragRafRef.current = null;
            return;
        }

        const scroller = rootRef.current?.closest?.(".chat-core-AO__messages");
        if (!scroller) {
            dragRafRef.current = null;
            return;
        }

        const rect = scroller.getBoundingClientRect();
        const y = dragRef.current.pointerY;
        const edge = Math.min(72, Math.max(42, rect.height * 0.12));
        let delta = 0;

        if (y < rect.top + edge) {
            delta = -Math.ceil((rect.top + edge - y) / 5);
        } else if (y > rect.bottom - edge) {
            delta = Math.ceil((y - (rect.bottom - edge)) / 5);
        }

        if (delta) {
            scroller.scrollTop += Math.max(-18, Math.min(18, delta));
            const pointerX = dragRef.current.pointerX || rect.left + rect.width / 2;
            addMessageAtPoint(pointerX, y);
        }

        dragRafRef.current = window.requestAnimationFrame(runAutoScroll);
    }, [addMessageAtPoint]);

    useEffect(() => {
        const handlePointerMove = (event) => {
            const drag = dragRef.current;
            if (!drag.active || event.pointerType !== "mouse") return;

            drag.pointerX = event.clientX;
            drag.pointerY = event.clientY;

            const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
            if (!drag.moved && distance >= 6) {
                drag.moved = true;
                suppressClickRef.current = true;
                document.body.classList.add("chat-drag-selecting-AO");
                if (drag.startMessage) onAddMessageSelection?.(drag.startMessage);
            }

            if (!drag.moved) return;

            event.preventDefault();
            addMessageAtPoint(event.clientX, event.clientY);

            if (!dragRafRef.current) {
                dragRafRef.current = window.requestAnimationFrame(runAutoScroll);
            }
        };

        const handlePointerUp = () => stopDragSelection();

        document.addEventListener("pointermove", handlePointerMove, { passive: false });
        document.addEventListener("pointerup", handlePointerUp);
        document.addEventListener("pointercancel", handlePointerUp);

        return () => {
            document.removeEventListener("pointermove", handlePointerMove);
            document.removeEventListener("pointerup", handlePointerUp);
            document.removeEventListener("pointercancel", handlePointerUp);
            stopDragSelection();
        };
    }, [addMessageAtPoint, onAddMessageSelection, runAutoScroll, stopDragSelection]);

    useEffect(() => () => {
        if (longPressTimerRef.current) window.clearTimeout(longPressTimerRef.current);
    }, []);

    const handleMessagePointerDown = (event, message) => {
        if (!canSelectMessage(message) || isInteractiveTarget(event.target)) return;

        if (event.pointerType === "mouse") {
            if (event.button !== 0) return;

            // В истории чата ЛКМ отвечает за нашу drag-selection механику,
            // поэтому нативное синее выделение текста браузером не запускаем.
            event.preventDefault();

            dragRef.current = {
                active: true,
                moved: false,
                pointerX: event.clientX,
                pointerY: event.clientY,
                startX: event.clientX,
                startY: event.clientY,
                startMessage: message,
            };
            return;
        }

        if (selectionMode) return;

        longPressStartRef.current = { x: event.clientX, y: event.clientY };
        if (longPressTimerRef.current) window.clearTimeout(longPressTimerRef.current);

        longPressTimerRef.current = window.setTimeout(() => {
            suppressClickRef.current = true;
            openActionMenu(message, event.clientX, event.clientY);
            longPressTimerRef.current = null;
        }, 520);
    };

    const handleMessagePointerMove = (event) => {
        if (event.pointerType === "mouse" || !longPressTimerRef.current || !longPressStartRef.current) return;

        const distance = Math.hypot(
            event.clientX - longPressStartRef.current.x,
            event.clientY - longPressStartRef.current.y
        );

        if (distance > 10) {
            window.clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const handleMessagePointerEnd = () => {
        if (longPressTimerRef.current) {
            window.clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        longPressStartRef.current = null;
    };

    if (!messages.length) {
        return (
            <div className="chat-messages-AO__empty">
                <span>Сообщений пока нет</span>
                <small>Первое сообщение появится здесь.</small>
            </div>
        );
    }

    return (
        <div className="chat-messages-AO" ref={rootRef}>
            {dayGroups.map((group) => (
                <section className="chat-day-AO" key={group.dayKey}>
                    <div className="chat-date-AO"><span>{group.label}</span></div>

                    {group.messages.map((message, index) => {
                        const previous = index > 0 ? group.messages[index - 1] : null;

                if (message.type === "call" || message.call) {
                    const call = message.call || {};
                    const outgoing = getId(call.initiator) === getId(currentUserId);

                    return (
                        <Fragment key={message._id}>
                            <div className={`chat-message-AO chat-message-AO--call ${outgoing ? "is-self" : "is-other"}`}>
                                <div className="chat-message-AO__call">
                                    <div className="chat-message-AO__call-icon">☎</div>
                                    <div>
                                        <div className="chat-message-AO__call-title">
                                            {outgoing ? "Исходящий" : "Входящий"} {call.video ? "видеозвонок" : "звонок"}
                                        </div>
                                        <div className="chat-message-AO__call-sub">
                                            {formatCallStatus(call.status)}
                                            {typeof call.durationSec === "number" && call.durationSec > 0
                                                ? ` · ${Math.floor(call.durationSec / 60)}:${String(call.durationSec % 60).padStart(2, "0")}`
                                                : ""}
                                        </div>
                                    </div>
                                </div>
                                <div className="chat-message-AO__meta">{formatTime(message.createdAt)}</div>
                            </div>
                        </Fragment>
                    );
                }

                const senderId = getId(message.sender);
                const isSelf = senderId === getId(currentUserId);
                const readByIds = Array.isArray(message.readBy) ? message.readBy.map(getId) : [];
                const readByOther = readByIds.some((id) => id && id !== getId(currentUserId));
                const reply = getReply(message);
                const bodyText = reply ? reply.body : message.text || "";
                const isFocused = focusMessageId && String(focusMessageId) === String(message._id);
                const isCard = ["product_card", "order_card"].includes(message.messageKind) || (message.entity?.type && message.entity.type !== "none");
                const hasAttachment = hasSecureChatAttachment(message);
                const compact = canGroupWithPrevious(message, previous);
                const sendStatus = message?.__sendStatus || null;
                const inlineMeta = !!bodyText && !hasAttachment && !isCard;
                const forwardedInfo = getForwardedInfo(message, currentUserId, chats);
                const bulkSelected = selectedSet.has(getId(message._id));

                return (
                    <Fragment key={message._id}>
                        <div
                            data-chat-message-id={message._id}
                            className={[
                                "chat-message-row-AO",
                                isSelf ? "is-self" : "is-other",
                                bulkSelected ? "is-bulk-selected" : "",
                            ].filter(Boolean).join(" ")}
                            onContextMenu={(event) => {
                                if (!canSelectMessage(message) || isInteractiveTarget(event.target)) return;
                                event.preventDefault();
                                event.stopPropagation();
                                openActionMenu(message, event.clientX, event.clientY);
                            }}
                            onPointerDown={(event) => handleMessagePointerDown(event, message)}
                            onPointerMove={handleMessagePointerMove}
                            onPointerUp={handleMessagePointerEnd}
                            onPointerCancel={handleMessagePointerEnd}
                            onDragStart={(event) => event.preventDefault()}
                            onClick={(event) => {
                                if (suppressClickRef.current) {
                                    suppressClickRef.current = false;
                                    event.preventDefault();
                                    event.stopPropagation();
                                    return;
                                }

                                if (selectionMode && canSelectMessage(message) && !isInteractiveTarget(event.target)) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onSelectMessage?.(message);
                                }
                            }}
                        >
                            <div
                                id={`chat-message-${message._id}`}
                                className={[
                                    "chat-message-AO",
                                    isSelf ? "is-self" : "is-other",
                                    compact ? "is-compact" : "",
                                    selectedId === message._id ? "is-selected" : "",
                                    isFocused ? "is-focused" : "",
                                    message.isDeleted ? "is-deleted" : "",
                                    isCard ? "is-card" : "",
                                ].filter(Boolean).join(" ")}
                            >
                                {!compact && (
                                    <div className="chat-message-AO__head">
                                        <div className="chat-message-AO__sender">
                                            {getSenderName(message.sender, currentUserId, message.guestSender)}
                                        </div>
                                    </div>
                                )}

                                {!!forwardedInfo && (
                                    <div className="chat-message-AO__forwarded">
                                        <span>Переслано от {forwardedInfo.authorText}</span>

                                        {forwardedInfo.showSourceChat && (
                                            forwardedInfo.sourceAccessible && onOpenForwardOrigin ? (
                                                <button
                                                    type="button"
                                                    data-chat-no-select="true"
                                                    title="Открыть исходное сообщение"
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        onOpenForwardOrigin(forwardedInfo.origin);
                                                    }}
                                                >
                                                    из «{forwardedInfo.sourceTitle}»
                                                </button>
                                            ) : (
                                                <span className="chat-message-AO__forwarded-source">
                                                    из «{forwardedInfo.sourceTitle}»
                                                </span>
                                            )
                                        )}
                                    </div>
                                )}

                                {reply && (
                                    <div className="chat-message-AO__quote">
                                        <div className="chat-message-AO__quote-author">{reply.author}</div>
                                        <div className="chat-message-AO__quote-text">{reply.snippet}</div>
                                    </div>
                                )}

                                {isCard && (
                                    <MessageEntityCard
                                        entity={message.entity}
                                        officeId={officeId}
                                    />
                                )}

                                {!!bodyText && (
                                    <div className={`chat-message-AO__text ${inlineMeta ? "has-inline-meta" : ""}`}>
                                        {bodyText}
                                    </div>
                                )}

                                {hasAttachment && (
                                    <Attachment
                                        message={message}
                                        onOpenImage={() => setOpenImageId(message._id)}
                                    />
                                )}

                                <div
                                    className={[
                                        "chat-message-AO__meta",
                                        inlineMeta ? "is-inline" : "",
                                    ].filter(Boolean).join(" ")}
                                >
                                    <span>{formatTime(message.createdAt)}</span>

                                    {isSelf && sendStatus === "sending" && (
                                        <span
                                            className="chat-message-AO__pending"
                                            title="Отправляется"
                                            aria-label="Отправляется"
                                        >
                                            ◷
                                        </span>
                                    )}

                                    {isSelf && sendStatus === "failed" && (
                                        <span
                                            className="chat-message-AO__send-failed"
                                            title={message?.__sendError || "Не удалось отправить"}
                                            aria-label="Ошибка отправки"
                                        >
                                            !
                                        </span>
                                    )}

                                    {isSelf && !sendStatus && (
                                        <span
                                            className="chat-message-AO__read"
                                            title={readByOther ? "Прочитано" : "Отправлено"}
                                        >
                                            {readByOther ? "✔✔" : "✔"}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Fragment>
                );
                    })}
                </section>
            ))}

            <MessageActionsMenu
                open={!!actionMenu.message}
                message={actionMenu.message}
                isSelf={getId(actionMenu.message?.sender) === getId(currentUserId)}
                anchorPoint={actionMenu.point}
                onClose={closeActionMenu}
                onReply={() => onReply?.(actionMenu.message)}
                onDelete={getId(actionMenu.message?.sender) === getId(currentUserId)
                    ? () => onDelete?.(actionMenu.message?._id)
                    : null}
                onForward={() => onForward?.(actionMenu.message)}
                onSelect={() => onAddMessageSelection?.(actionMenu.message)}
            />

            <SiteMediaViewer
                open={!!openImageId && imageMessages.length > 0}
                items={imageMessages}
                activeIndex={openImageIndex}
                onActiveIndexChange={handleImageIndexChange}
                onClose={() => setOpenImageId(null)}
                resolveItem={resolveImage}
                getItemMimeType={(message) => getChatAttachmentMimeType(message) || "image/jpeg"}
                getTitle={(message) => getChatAttachmentName(message, "Фото из чата")}
                getSubtitle={(message) => (
                    `${getSenderName(message?.sender, currentUserId, message?.guestSender)} · ${formatTime(message?.createdAt)}`
                )}
                getAlt={(message) => getChatAttachmentName(message, "Фото из чата")}
                onDownload={handleDownload}
                onShare={handleShare}
                shareLabel="Поделиться"
                loadingText="Загружаем защищённое фото…"
                emptyText="Фото недоступно"
            />
        </div>
    );
}
